import { Category, CategoryFormData } from "@/model/entities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { categoryService } from "@/service/categoryService";
import { toast } from "sonner";

import i18next from "i18next";

export function useCategories(restaurantId: string) {
  const qc = useQueryClient();

  const query = useQuery<Category[]>({
    queryKey: ["categories", restaurantId],
    queryFn: () => categoryService.fetchAll(restaurantId),
    enabled: !!restaurantId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const createCategory = useMutation({
    mutationFn: (category: CategoryFormData) => {
      // Pass a default order value (e.g., 0) since CategoryFormData does not have 'order'
      return categoryService.create(restaurantId, category, 0);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["categories", restaurantId] });
    },
  });

  const saveCategories = useMutation({
    mutationFn: (categories: Category[]) => {
      return categoryService.updateBulk(categories);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["categories", restaurantId] });
    },
  });

  const updateCategory = useMutation({
    mutationFn: ({
      categoryId,
      category,
    }: {
      categoryId: string;
      category: CategoryFormData;
    }) => {
      if (!categoryId) throw new Error("Category id is required");
      return categoryService.update(categoryId, category);
    },
    // Recommended optimistic update flow from react-query docs
    onMutate: async ({
      categoryId,
      category,
    }: {
      categoryId: string;
      category: CategoryFormData;
    }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await qc.cancelQueries({ queryKey: ["categories", restaurantId] });

      // Snapshot the previous value
      const previousCategories = qc.getQueryData<Category[]>([
        "categories",
        restaurantId,
      ]);

      // Debug: log current cache for diagnosis
      if (process.env.NODE_ENV !== "production") {
        console.debug("[useCategories] onMutate - queryKey:", [
          "categories",
          restaurantId,
        ]);
        console.debug(
          "[useCategories] onMutate - previousCategories:",
          previousCategories
        );
      }

      // Optimistically update the cache
      qc.setQueryData(
        ["categories", restaurantId],
        (old: Category[] | undefined) => {
          if (!old) return old;
          return old.map((cat) =>
            cat.id === categoryId ? { ...cat, ...category } : cat
          );
        }
      );

      // Return context for potential rollback
      return { previousCategories };
    },
    onSuccess: (data, variables) => {
      // Replace the updated category with the server response (ensures server-generated fields are applied)
      const { categoryId } = variables as {
        categoryId: string;
        category: CategoryFormData;
      };
      qc.setQueryData(
        ["categories", restaurantId],
        (old: Category[] | undefined) => {
          if (!old) return old;
          return old.map((cat) =>
            cat.id === categoryId ? (data as Category) : cat
          );
        }
      );
      // Use i18next translation for success message, default in Hebrew
      toast.success(
        i18next.t("category.updated_success", "הקטגוריה עודכנה בהצלחה")
      );
    },
    onError: (error, _variables, context) => {
      console.error("Error updating category:", error);
      toast.error(
        i18next.t(
          "category.update_failed",
          "Failed to update category. Please try again."
        )
      );
      // Rollback to previous categories snapshot
      if (context?.previousCategories) {
        qc.setQueryData(
          ["categories", restaurantId],
          context.previousCategories
        );
      }
    },
    onSettled: () => {
      // Ensure we have the latest from server
      qc.invalidateQueries({ queryKey: ["categories", restaurantId] });
    },
  });

  const deleteCategory = useMutation({
    mutationFn: (id: string) => {
      if (!id) throw new Error("Category id is required");
      return categoryService.delete(id);
    },
    onSuccess: () => {
      // Invalidate categories cache
      qc.invalidateQueries({ queryKey: ["categories", restaurantId] });
      // Also invalidate menu items cache since we deleted menu items
      qc.invalidateQueries({ queryKey: ["menuItems"] });
    },
  });

  const toggleHidden = useMutation({
    mutationFn: async ({
      categoryId,
      isHidden,
    }: {
      categoryId: string;
      isHidden: boolean;
    }) => {
      if (!categoryId) throw new Error("Category id is required");
      return categoryService.toggleHidden(categoryId, isHidden);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["categories", restaurantId] });
    },
  });

  return {
    ...query,
    updateCategory,
    deleteCategory,
    createCategory,
    saveCategories,
    toggleHidden,
  };
}
